import React from 'react';

import { useTheme } from '@course-authoring/shared-types';
import { useTranslation } from '@course-authoring/i18n';

import { ActionButton } from './ActionButton';
const SUBMITTING_MIN_WIDTH = '188px';

export interface SubmitRevealButtonProps {
  /** True while no selection has been made (disabled treatment shows). */
  disabled: boolean;
  /** `isSubmitting` from `useSubmitRevealTransition`. */
  isSubmitting: boolean;
  prefersReducedMotion: boolean;
  onSubmit: () => void;
  /** Block-specific submit-button base style (min-width, margin, …). */
  baseStyle: React.CSSProperties;
  /** Disabled treatment (e.g. `styles.likertSubmitButtonDisabled`). */
  disabledStyle: React.CSSProperties;
  /** Hover treatment (e.g. `styles.actionButtonHover`). */
  hoverStyle: React.CSSProperties;
  /** Button background while the submitting choreography plays. */
  accentColor: string;
  /**
   * Optional label/check color while submitting (e.g. dark text on the
   * WCS yellow accent). When provided, `color` also joins the button's
   * transition list so the swap animates.
   */
  submittingTextColor?: string;
  'data-testid'?: string;
}

/**
 * Submit button for interactions that use the shared submit-to-results
 * reveal choreography (`useSubmitRevealTransition`): the "Submit" label
 * lifts out while a "✓ Showing results" status overlay fades in, the
 * button compresses (translateY(-6px) scale(0.98)) and widens to 188px,
 * and the background shifts to the answer's accent color.
 */
export function SubmitRevealButton({
  disabled,
  isSubmitting,
  prefersReducedMotion,
  onSubmit,
  baseStyle,
  disabledStyle,
  hoverStyle,
  accentColor,
  submittingTextColor,
  'data-testid': dataTestId,
}: SubmitRevealButtonProps): React.ReactElement {
  const { tokens } = useTheme();
  const { t } = useTranslation();
  const SUBMIT_LABEL = t('submit_reveal.submit');
  const SUBMITTING_STATUS_TEXT = t('submit_reveal.showing_results');

  const transitionProperties = [
    'background-color',
    ...(submittingTextColor !== undefined ? ['color'] : []),
    'transform',
    'min-width',
    'opacity',
  ];
  const buttonOverrideStyle: React.CSSProperties = {
    position: 'relative',
    overflow: 'hidden',
    ...(isSubmitting
      ? {
          minWidth: SUBMITTING_MIN_WIDTH,
          backgroundColor: accentColor,
          ...(submittingTextColor !== undefined ? { color: submittingTextColor } : {}),
        }
      : {}),
    transform: isSubmitting ? 'translateY(-6px) scale(0.98)' : 'translateY(0) scale(1)',
    transition: prefersReducedMotion
      ? 'none'
      : transitionProperties
          .map((property) => `${property} ${tokens.ui.transitionDefault}`)
          .join(', '),
  };
  const primaryLabelStyle: React.CSSProperties = prefersReducedMotion
    ? {}
    : {
        opacity: isSubmitting ? 0 : 1,
        transform: isSubmitting ? 'translateY(-8px)' : 'translateY(0)',
        transition: `opacity ${tokens.ui.transitionDefault}, transform ${tokens.ui.transitionDefault}`,
      };
  const statusOverlayStyle: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacing.sm,
    opacity: isSubmitting ? 1 : 0,
    transform: isSubmitting ? 'translateY(0)' : 'translateY(8px)',
    transition: prefersReducedMotion
      ? 'none'
      : `opacity ${tokens.ui.transitionDefault}, transform ${tokens.ui.transitionDefault}`,
  };

  return (
    <ActionButton
      aria-label={isSubmitting ? SUBMITTING_STATUS_TEXT : SUBMIT_LABEL}
      onClick={onSubmit}
      disabled={disabled || isSubmitting}
      disabledStyleActive={disabled}
      baseStyle={baseStyle}
      disabledStyle={disabledStyle}
      hoverStyle={hoverStyle}
      styleOverride={buttonOverrideStyle}
      data-testid={dataTestId}
    >
      <span aria-hidden={isSubmitting} style={primaryLabelStyle}>
        {SUBMIT_LABEL}
      </span>
      <span aria-hidden={!isSubmitting} style={statusOverlayStyle}>
        <span
          aria-hidden="true"
          style={{
            fontSize: '18px',
            fontWeight: 700,
            lineHeight: 1,
            ...(submittingTextColor !== undefined ? { color: submittingTextColor } : {}),
          }}
        >
          ✓
        </span>
        {SUBMITTING_STATUS_TEXT}
      </span>
    </ActionButton>
  );
}

SubmitRevealButton.displayName = 'SubmitRevealButton';
